import { Box, Stack, Typography } from '@mui/material';

import { FormAwareDatePickerInput } from '@/components/form/formAwareInputs/FormAwareDatePickerInput';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { StructuredAssetsSubform_ValuationFragment } from '@/modules/entities/StructuredAssetsSubform/graphql/StructuredAssetsSubform.generated';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';

import {
  FullScreenStructuredAssetsModalForm,
  FullScreenStructuredAssetsModalFormPath,
} from './FullScreenStructuredAssetsModal.types';
import { useTotalAssetValueSum } from './hooks/useTotalAssetValueSum';
import { useValuationIntegrationDetails } from './hooks/useValuationLinkDetails';

interface ValuationDateFieldProps {
  valuation: StructuredAssetsSubform_ValuationFragment | null;
}

export function ValuationDateField({ valuation }: ValuationDateFieldProps) {
  const { control } = useFormContext<FullScreenStructuredAssetsModalForm>();
  const { isIntegratedScenarioDirty, isIntegratedScenario } =
    useValuationIntegrationDetails();

  const totalAssetValueSum = useTotalAssetValueSum(valuation);

  const isEditableValuationDate = !isIntegratedScenario;

  // We don't have any kind of date-related information to show here; the valuation date
  // will be created when we get the initial sync.
  const isIntegratedScenarioWithoutValuation =
    isIntegratedScenario && !valuation;

  // If there have been changes to the integration setup, but we're still in an integrated state,
  // we don't want to show the date
  const isNotYetSyncedIntegrationScenario =
    isIntegratedScenarioDirty && isIntegratedScenario;
  if (
    isNotYetSyncedIntegrationScenario ||
    isIntegratedScenarioWithoutValuation
  ) {
    return null;
  }

  return (
    <Card variant="filled-callout" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="end"
        justifyContent="flex-end"
      >
        <Typography variant="title-l" component="span">
          {formatCurrencyNoDecimals(totalAssetValueSum)}
        </Typography>
        <Typography
          variant="body-s"
          component="span"
          sx={{ pb: 0.25, flexShrink: 0 }}
        >
          as of
          {!isEditableValuationDate && valuation
            ? ` ${formatDateToMMDDYY(valuation?.effectiveDate)}`
            : ''}
        </Typography>
        {isEditableValuationDate && (
          <Box width={175}>
            <FormAwareDatePickerInput
              control={control}
              label="Valuation date"
              required
              hideLabel
              fieldName={
                `valuationAsOfDate` as const satisfies FullScreenStructuredAssetsModalFormPath
              }
              popperPlacement="top"
            />
          </Box>
        )}
      </Stack>
    </Card>
  );
}
